import { init } from '@plausible-analytics/tracker';
import { RampProvider } from '@ramp/components';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AppLoader from 'components/AppLoader';
import SomethingWentWrong from 'components/SomethingWentWrong';
import dayjs from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import minMax from 'dayjs/plugin/minMax';
import relativeTimePlugin from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import UTC from 'dayjs/plugin/utc';
import i18next from 'i18next';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Router from 'router';
import { useLayoutStore } from 'store/layout';
import { APIError } from 'types';
import { setDayJsLocale } from 'utils/_utils/date';
import { getEnvironment } from 'utils/_utils/environment';
import 'utils/fonts';
import { notify } from 'utils/notifications/toast';

import './main.css';

import 'dayjs/locale/cs';
import 'dayjs/locale/de';

import pkg from '../package.json';

dayjs.extend(isBetween);
dayjs.extend(durationPlugin);
dayjs.extend(relativeTimePlugin);
dayjs.extend(UTC);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(minMax);
dayjs.extend(updateLocale);

dayjs.updateLocale('cs', {
  months: [
    'Ledna',
    'Února',
    'Března',
    'Dubna',
    'Května',
    'Června',
    'Července',
    'Srpna',
    'Září',
    'Října',
    'Listopadu',
    'Prosince'
  ],
  weekdays: ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'],
  weekdaysShort: ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So']
});

const App: React.FC = () => {
  const { t } = useTranslation();
  const loading = useLayoutStore(store => store.loading);
  const [brandColorSchemeLight, brandColorSchemeDark] = useLayoutStore(store => [
    store.theme.color_scheme_light,
    store.theme.color_scheme_dark
  ]);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // @ts-ignore
            onError: (err: APIError) => {
              notify.error({
                title: t('components.notification.error', { code: err.code_name || err.code }),
                description: err.description
              });

              // If the error is 4xx, redirect to the home page
              if (400 <= (err.code || 0) && (err.code || 0) <= 499) {
                window.location.replace('/');
              }
            },
            cacheTime: 0,
            staleTime: Infinity,
            refetchOnWindowFocus: false
          }
        }
      }),
    []
  );

  useEffect(() => {
    const env = getEnvironment();

    if (env !== 'prod') return;

    init({
      domain: 'app.redamp.io',
      endpoint: 'https://stats.redamp.io/api/event'
    });
  }, []);

  useEffect(() => {
    // Set initial dayjs locale
    setDayJsLocale(localStorage.getItem('i18nextLng') || 'en');

    // Listen for language changes, so the dayjs locale (date format for the specific language) is updated
    i18next.on('languageChanged', setDayJsLocale);

    return () => {
      i18next.off('languageChanged', setDayJsLocale);
    };
  }, []);

  return (
    <RampProvider
      brand={{ light: brandColorSchemeLight, dark: brandColorSchemeDark }}
      initialColorMode="dark"
      appVersion={`v${pkg.version}`}
    >
      <SentryErrorBoundary fallback={<SomethingWentWrong />}>
        <QueryClientProvider client={queryClient}>{loading ? <AppLoader /> : <Router />}</QueryClientProvider>
      </SentryErrorBoundary>
    </RampProvider>
  );
};

export default App;
